import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface InterpretResult {
  queryString: string;
  understood: string[];
  usedModel: boolean;
  message?: string | null;
}

/** The examples under the box, which exist to say that a sentence is allowed here at all. */
export const SEARCH_EXAMPLES = [
  'Porodični auto do 10.000 €',
  'Mali gradski auto do 4.000 €',
  'Nemac limuzina dizel, malo jači',
  'Sportski benzinac, kupe',
];

/**
 * Whether every filter the interpreter set is still in the URL.
 *
 * Keys the interpreter never touched are ignored, because the sort order and the buyer's own
 * sentence ride alongside without being part of what was interpreted. Anything it did set and the
 * buyer has since changed means the explanation is describing a search that is no longer on screen
 * — and a note listing ten chosen models above a search narrowed by hand is worse than no note.
 */
function stillApplied(applied: string, current: string): boolean {
  const live = new URLSearchParams(current);
  const wanted = new URLSearchParams(applied);

  for (const key of new Set(wanted.keys())) {
    const mine = wanted.getAll(key).slice().sort();
    const theirs = live.getAll(key).slice().sort();

    if (mine.length !== theirs.length || mine.some((value, index) => value !== theirs[index])) {
      return false;
    }
  }

  return true;
}

/**
 * One search box for both kinds of search.
 *
 * Every query goes through the interpreter, because there is no way for a buyer to know in advance
 * which kind theirs is. "Audi Q8" is a marque and a model, "50000km" is a number with a unit,
 * "nemac" is a reputation and "crveni kabriolet" is two filters — all four are things people type
 * into the same box, and asking them to first classify their own sentence and pick the matching
 * field was the whole problem with having two.
 *
 * What comes back is a query string, so the result is an ordinary search: refinable in the panel,
 * shareable as a link, and reversible with the back button. Anything the interpreter could not read
 * comes back as a free-text term instead, which is exactly what the old keyword box did — so the
 * merge loses nothing.
 */
export function useSmartSearch(apply: (params: URLSearchParams) => void, currentSearch: string) {
  const interpret = useMutation({
    mutationFn: (text: string) =>
      api<InterpretResult>('/api/search/interpret', { method: 'POST', body: { prompt: text } }),
    onSuccess: (data, text) => {
      const params = new URLSearchParams(data.queryString);

      /*
        The sentence is only carried alongside when it became filters. Where it did not, the
        interpreter has already put it in `q` and the box reads it back from there — keeping both
        would show the same words twice, once in the box and once as a chip under it.
      */
      if (!params.has('q')) {
        params.set('qs', text);
      }

      apply(params);
    },
    // The interpreter being unreachable must not mean the search button does nothing. Falling back
    // to a plain keyword search is exactly what the old second box would have done with the words.
    onError: (_error, text) => apply(new URLSearchParams({ q: text })),
  });

  const search = (text: string) => {
    const trimmed = text.trim();

    if (trimmed.length > 0 && !interpret.isPending) {
      interpret.mutate(trimmed);
    }
  };

  /*
    What the last interpretation put in the URL, whichever way it went.

    A failed call still applies a search — the buyer's words as a free-text term — so it has an
    applied search of its own, and its explanation has to retire with that search exactly like the
    successful one does. Without this the "tražimo po tekstu" line outlives the `q` it describes and
    sits above a search that has nothing to do with it until the next submission.
  */
  const applied = interpret.isError
    ? interpret.variables === undefined
      ? null
      : new URLSearchParams({ q: interpret.variables }).toString()
    : (interpret.data?.queryString ?? null);

  /*
    Three things the chips cannot say for themselves: that nothing was recognised and the words are
    being searched for instead, that a range the buyer typed ran backwards and was dropped — the one
    filter no chip can show, because it is not there — and which models a phrase like "porodični" was
    answered with.
  */
  const note = interpret.isError
    ? 'Nismo uspeli da pročitamo pretragu — tražimo po tekstu.'
    : (interpret.data?.message ?? null);

  return {
    search,
    pending: interpret.isPending,
    message: note !== null && applied !== null && stillApplied(applied, currentSearch) ? note : null,
  };
}
